/**
 * Agent personas — what makes the roster feel like a *team of people*, not a
 * list of jobs. Each agent gets a human job title + a deterministic DiceBear
 * avatar (MIT, free CDN — same seed → same face). The avatar STYLE is chosen by
 * persona, so the team reads as a real mix:
 *   - adventurer       → the front-facing humans (leads, outreach, lifecycle)
 *   - lorelei-neutral  → the writers' room (brief/draft/seo/humanize, copy, QA)
 *   - toon-head        → the creatives (designers, video, tooling)
 *   - bottts           → the ops robots (indexing, monitors — no human analog)
 *   - lorelei (Moxxie) → the CMO herself
 *
 * Pure UI helper (no host imports). Avatars render via plain <img>. If a strict
 * prod CSP blocks the CDN, add `https://api.dicebear.com` to img-src (or swap to
 * the @dicebear npm package for fully-local SVG generation).
 */

/** Human job title per agent — "a real human in the role." */
export const AGENT_ROLES: Record<string, string> = {
  'content-orchestrator': 'Head of Content',
  'content-brief': 'Content Strategist',
  'content-draft': 'Staff Writer',
  'content-seo': 'SEO Specialist',
  'content-humanize': 'Line Editor',
  'seo-auditor': 'Technical SEO Lead',
  'pipeline-triage': 'Managing Editor',
  indexnow: 'Indexing Bot',
  'domain-authority': 'Authority Tracker',
  'pseo-content': 'pSEO Writer',
  'content-audit': 'QA Editor',
  copywriter: 'Copywriter',
  'gtm-copy-landing': 'Landing Copywriter',
  'blog-featured-images': 'Brand Designer',
  directory: 'Backlinks Lead',
  'marketing-images': 'Graphic Designer',
  'marketing-video': 'Video Editor',
  'cold-email': 'Head of Outbound',
  'warmup-monitor': 'Deliverability Bot',
  'mailbox-health': 'Inbox Monitor',
  'domain-health': 'Domain Monitor',
  'lifecycle-email': 'Lifecycle Manager',
  'free-tools': 'Head of PLG',
  'free-tool-ship': 'Tools Engineer',
  'reddit-warmup': 'Community Manager',
  producthunt: 'Launch Lead',
  indiehackers: 'Build-in-Public Lead',
  'youtube-outreach': 'Creator Partnerships',
};

export function agentRole(id: string): string {
  return AGENT_ROLES[id] ?? 'Specialist';
}

/** DiceBear style per agent — chosen to match persona. Default = adventurer (human). */
const AGENT_STYLE: Record<string, string> = {
  // Ops robots — pure automation / monitoring, no human analog
  indexnow: 'bottts',
  'domain-authority': 'bottts',
  'mailbox-health': 'bottts',
  'domain-health': 'bottts',
  'warmup-monitor': 'bottts',
  // Writers' room — clean, neutral illustrated humans
  'content-brief': 'lorelei-neutral',
  'content-draft': 'lorelei-neutral',
  'content-seo': 'lorelei-neutral',
  'content-humanize': 'lorelei-neutral',
  'pseo-content': 'lorelei-neutral',
  'content-audit': 'lorelei-neutral',
  copywriter: 'lorelei-neutral',
  'gtm-copy-landing': 'lorelei-neutral',
  // Creatives — playful cartoon heads
  'blog-featured-images': 'toon-head',
  'marketing-images': 'toon-head',
  'marketing-video': 'toon-head',
  'free-tool-ship': 'toon-head',
  // The CMO
  moxxie: 'lorelei',
};

const DEFAULT_STYLE = 'adventurer';

/**
 * Brand-tinted disc backgrounds. The line-art styles (lorelei/bottts) are dark
 * strokes that vanish on the #0a0a0a UI — a solid colored disc (deterministic
 * per seed) makes every face pop while staying in the purple→pink→amber family.
 */
const AVATAR_BG = '8b5cf6,a78bfa,c4b5fd,ec4899,f472b6,f59e0b,fbbf24';

/** Moxxie — the CMO. She wears the confident pink-topknot-and-shades face (the
 *  `cold-email` adventurer seed, swapped in by request), on a brand purple→pink
 *  gradient disc so she still reads as the lead. Verified render. */
const MOXXIE_AVATAR =
  'https://api.dicebear.com/9.x/adventurer/svg?seed=cold-email' +
  '&backgroundColor=8b5cf6,a78bfa&backgroundType=gradientLinear';

/** The cheerful-woman face Moxxie used to wear now belongs to the Head of Outbound,
 *  so the two never show up as twins on the same page. */
const COLD_EMAIL_AVATAR =
  'https://api.dicebear.com/9.x/adventurer/svg?seed=Moxxie' +
  `&backgroundColor=${AVATAR_BG}&backgroundType=solid` +
  '&hair=long02,long03,long04,long07,long12,long16,long20' +
  '&hairColor=2c1b18,562306,724133,a55728' +
  '&mouth=variant04,variant10,variant12,variant16,variant19,variant21,variant26,variant30' +
  '&eyes=variant02,variant04,variant09,variant12,variant21,variant24';

/** Support — Sage, a warm, confident female assistant face. Constrained to FRIENDLY
 *  mouth + open-eye variants only (no scowls/grimaces). Brand-family pink→amber
 *  gradient disc so she blends with the dark admin like Tosh does (Tosh runs the
 *  full violet→pink→amber 3-stop; Sage uses the warm 2-stop half so she stays
 *  visually distinct from BOTH the founder AND Moxxie's pink/violet halo). Seed
 *  locked ('Sage') because the variant lottery can otherwise land on a grumpy
 *  combo — verified visually before committing this seed. */
const SUPPORT_AVATAR =
  'https://api.dicebear.com/9.x/adventurer/svg?seed=Sage' +
  '&backgroundColor=EC4899,F59E0B&backgroundType=gradientLinear' +
  '&hair=long01,long05,long09,long15,long21' +
  '&hairColor=2c1b18,562306,724133,a55728' +
  '&mouth=variant16,variant19,variant21,variant26,variant30' +
  '&eyes=variant02,variant04,variant09,variant12' +
  '&eyebrows=variant09,variant12,variant13,variant15';

/** Tosh (founder) — confident dark-skin male face with smart glasses + soft smile.
 *  Lives on the brand violet→pink halo (same family as CMO Moxxie so the founder
 *  reads as kin to the lead). Seed 'Founder' verified visually — locked because
 *  the variant lottery can land on goofy/cocky combos (tongue-out, smirk). Hair
 *  palette has NO green entries; mouth variants are all neutral/smiling (no
 *  tongue-out); glasses on by default (looks sharper than not). */
const TOSH_AVATAR =
  'https://api.dicebear.com/9.x/adventurer/svg?seed=Founder' +
  '&backgroundColor=8b5cf6,ec4899&backgroundType=gradientLinear' +
  '&skinColor=614335,8d5524' +
  '&hair=short01,short02,short03,short04,short05,short07,short11,short13' +
  '&hairColor=0e0e0e,2c1b18,562306,724133' +
  '&mouth=variant16,variant19,variant21,variant26,variant30' +
  '&eyes=variant02,variant04,variant09,variant12' +
  '&eyebrows=variant09,variant12,variant13,variant15' +
  '&facialHair=beardLight,beardMustache' +
  '&facialHairProbability=50' +
  '&glasses=variant01,variant02,variant05' +
  '&glassesProbability=100';

/**
 * Deterministic illustrated avatar for a real PERSON (a customer in support, an
 * anonymous visitor — anyone who isn't an internal agent/persona). Picks from a
 * warm, neutral palette and a person-style adventurer-neutral set so the face
 * never duplicates the brand-pink Moxxie/cold-email halo. Seed = stable identity
 * (userId, guest email hash) so a returning user always sees the same face.
 */
export function userAvatar(seed: string | number): string {
  const s = String(seed);
  // Pre-picked neutral disc colors that aren't in the agent rotation — so the user
  // is visually distinct from internal personas at a glance.
  const PALETTE = ['475569', '64748b', '0d9488', '0e7490', '6366f1', '78716c', '4b5563'];
  const idx = [...s].reduce((acc, ch) => (acc + ch.charCodeAt(0)) | 0, 0) % PALETTE.length;
  const bg = PALETTE[idx];

  return `https://api.dicebear.com/9.x/adventurer-neutral/svg?seed=${encodeURIComponent(s)}&backgroundColor=${bg}&backgroundType=solid`;
}

/**
 * Vibrant gradient "ring" per subscription tier — the colored band around support
 * avatars so tiers are noticeable at a glance and read as on-brand. Returns a CSS
 * gradient for a wrapper's background (the avatar sits on top with ~2px padding
 * showing the ring).
 */
export function tierRingGradient(tier: string): string {
  const m: Record<string, string> = {
    paid: 'linear-gradient(135deg, #34d399 0%, #22d3ee 100%)', // emerald → cyan
    trial: 'linear-gradient(135deg, #fbbf24 0%, #fb7185 100%)', // amber → rose
    anonymous: 'linear-gradient(135deg, #38bdf8 0%, #a78bfa 100%)', // sky → violet
    free: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%, #f59e0b 100%)', // brand
  };

  return m[tier] ?? m.free;
}

/** Deterministic illustrated avatar for an agent (or Moxxie), styled by persona. */
export function agentAvatar(seed: string): string {
  if (seed === 'moxxie') return MOXXIE_AVATAR;

  if (seed === 'cold-email') return COLD_EMAIL_AVATAR;

  if (seed === 'support') return SUPPORT_AVATAR;

  if (seed === 'tosh' || seed === 'founder') return TOSH_AVATAR;
  const style = AGENT_STYLE[seed] ?? DEFAULT_STYLE;
  const params = new URLSearchParams({ seed, backgroundColor: AVATAR_BG, backgroundType: 'solid' });

  return `https://api.dicebear.com/9.x/${style}/svg?${params.toString()}`;
}
